import { nativeDeepLinkPath, nativeOtaDeepLinkVersion } from './router/mobile-deep-link';
import { App as NativeApp } from '@capacitor/app';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications, type ActionPerformed, type Token } from '@capacitor/push-notifications';
import { CapacitorUpdater, type BundleInfo, type DownloadOptions } from '@capgo/capacitor-updater';
import { ref } from 'vue';
import type { Router } from 'vue-router';
import { apiBaseUrl, apiUrl } from './composables/api-url';
import { getApiToken } from './composables/api';
import { refreshConnectivityAfterResume } from './composables/connectivity';
import { offlineQueueCount } from './composables/offline-queue';
import { watchNativeHomeUpdate } from './composables/native-home-update';
import { createNativeUpdateController } from './composables/native-update';
import { createNativeOtaDeepLinkController } from './composables/native-ota-deep-link';
export type { NativeUpdatePhase } from './composables/native-update';
import {
  cancelOfflineReminders,
  initializeOfflineReminders,
  requestOfflineReminderPermission,
  scheduleOfflineReminders,
} from './composables/offline-reminders';

let lastPushToken = '';
let nativeInstallationId = '';
let responsibilityBadgeSync: Promise<void> | null = null;
const nativeAppActive = ref(true);
let nativeUpdateTrackingPromise: Promise<void> | null = null;
const pendingCountCachePrefix = 'eqover-pending-count-v1:';
const surveyPendingCountCachePrefix = 'eqover-survey-pending-count-v1:';
const reportedWidgetStatusPrefix = 'eqover-widget-status-reported-v1:';

interface ResponsibilityWidgetItem {
  id: string;
  title: string;
  priority: string;
}

interface EqoverWidgetPlugin {
  configure(options: { apiBaseUrl: string; accessToken: string }): Promise<void>;
  refresh(): Promise<void>;
  updateSnapshot(options: { pendingCount: number; responsibilities: ResponsibilityWidgetItem[] }): Promise<void>;
  getStatus(): Promise<{ installed: boolean; instances: number; pinSupported: boolean }>;
  requestPin(): Promise<{ supported: boolean; requested: boolean }>;
}

interface EqoverAppearancePlugin {
  setStatusBarStyle(options: { style: 'dark' | 'light'; backgroundColor?: string }): Promise<void>;
}

interface EqoverSharePlugin {
  shareFile(options: {
    base64Data: string;
    fileName: string;
    mimeType: string;
    title: string;
    text: string;
  }): Promise<void>;
  shareText(options: {
    title: string;
    text: string;
    url: string;
  }): Promise<void>;
}

const EqoverWidget = registerPlugin<EqoverWidgetPlugin>('EqoverWidget');
const EqoverAppearance = registerPlugin<EqoverAppearancePlugin>('EqoverAppearance');
const EqoverShare = registerPlugin<EqoverSharePlugin>('EqoverShare');

const otaDeepLinks = createNativeOtaDeepLinkController({
  resolve: async (version, signal) => {
    const appInfo = await NativeApp.getInfo();
    const response = await fetch(apiUrl('/api/mobile/ota/resolve'), {
      method: 'POST', signal, cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version, platform: Capacitor.getPlatform(), nativeVersionCode: appInfo.build }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error ?? 'No fue posible encontrar la versión OTA.');
    if (typeof payload.version !== 'string' || !/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/.test(payload.version)
      || (version !== 'latest' && payload.version !== version)
      || typeof payload.url !== 'string' || !payload.url.startsWith('https://')
      || typeof payload.checksum !== 'string' || !/^[a-f0-9]{64}$/.test(payload.checksum)) {
      throw new Error('La versión OTA recibida no es válida.');
    }
    return payload as DownloadOptions;
  },
  current: () => CapacitorUpdater.current(),
  download: (options) => CapacitorUpdater.download(options),
  set: (options) => CapacitorUpdater.set(options),
  delete: (options) => CapacitorUpdater.delete(options),
  storage: () => localStorage,
  launchStorage: () => sessionStorage,
});
export const nativeOtaDeepLinkState = otaDeepLinks.state;
export const requestCancelNativeOta = otaDeepLinks.requestCancel;
export const confirmCancelNativeOta = otaDeepLinks.confirmCancel;

const nativeUpdates = createNativeUpdateController({
  current: () => CapacitorUpdater.current(),
  set: (options) => CapacitorUpdater.set(options),
  storage: () => localStorage,
  blocked: (version) => otaDeepLinks.blocksAutomatic(version),
});
export const nativeUpdateState = nativeUpdates.state;

export function initializeNativeUpdateTracking(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve();
  if (nativeUpdateTrackingPromise) return nativeUpdateTrackingPromise;

  const reportError = (error: unknown) => console.error('No fue posible observar la actualización OTA.', error);
  nativeUpdateTrackingPromise = Promise.all([
    CapacitorUpdater.addListener('download', ({ percent, bundle }) => {
      otaDeepLinks.progress(percent, bundle);
      if (otaDeepLinks.blocksAutomatic(bundle.version)) return;
      void nativeUpdates.progress(percent, bundle).catch(reportError);
    }),
    CapacitorUpdater.addListener('downloadComplete', ({ bundle }) => { trackReadyBundle(bundle); }),
    CapacitorUpdater.addListener('updateAvailable', ({ bundle }) => { trackReadyBundle(bundle); }),
    CapacitorUpdater.addListener('setNext', ({ bundle }) => { trackReadyBundle(bundle); }),
    CapacitorUpdater.addListener('downloadFailed', ({ version }) => {
      if (otaDeepLinks.blocksAutomatic(version)) return;
      nativeUpdates.failed(version);
    }),
  ]).then(async () => {
    const pending = await CapacitorUpdater.getNextBundle().catch(() => null);
    if (pending && !otaDeepLinks.blocksAutomatic(pending.version)) await nativeUpdates.ready(pending);
  }).catch((error) => {
    nativeUpdateTrackingPromise = null;
    console.error('No fue posible observar las actualizaciones OTA.', error);
  });

  return nativeUpdateTrackingPromise;

  function trackReadyBundle(bundle: BundleInfo) {
    if (!otaDeepLinks.blocksAutomatic(bundle.version)) void nativeUpdates.ready(bundle).catch(reportError);
  }
}

export async function applyDownloadedNativeUpdate(automatic = false): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await nativeUpdates.apply(automatic);
}

type NativeStatusBarAppearance = { style: 'dark' | 'light'; backgroundColor?: string };
let baseStatusBarAppearance: NativeStatusBarAppearance = { style: 'dark', backgroundColor: '#ffffff' };
const statusBarOverrides = new Map<symbol, NativeStatusBarAppearance>();

export async function setNativeStatusBarStyle(style: 'dark' | 'light', backgroundColor?: string): Promise<void> {
  baseStatusBarAppearance = { style, backgroundColor };
  await applyNativeStatusBarAppearance(effectiveStatusBarAppearance());
}

export function overrideNativeStatusBarStyle(style: 'dark' | 'light', backgroundColor?: string): () => void {
  const token = Symbol('native-status-bar-override');
  statusBarOverrides.set(token, { style, backgroundColor });
  void applyNativeStatusBarAppearance(effectiveStatusBarAppearance());
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    statusBarOverrides.delete(token);
    void applyNativeStatusBarAppearance(effectiveStatusBarAppearance());
  };
}

function effectiveStatusBarAppearance(): NativeStatusBarAppearance {
  return [...statusBarOverrides.values()].at(-1) ?? baseStatusBarAppearance;
}

async function applyNativeStatusBarAppearance(appearance: NativeStatusBarAppearance): Promise<void> {
  if (!Capacitor.isNativePlatform() || !['android', 'ios'].includes(Capacitor.getPlatform())) return;
  await EqoverAppearance.setStatusBarStyle(appearance).catch(() => undefined);
}

export async function shareNativeFile(options: {
  blob: Blob;
  fileName: string;
  title: string;
  text: string;
}): Promise<boolean> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return false;
  const base64Data = await blobToBase64(options.blob);
  await EqoverShare.shareFile({
    base64Data,
    fileName: options.fileName,
    mimeType: options.blob.type || 'application/octet-stream',
    title: options.title,
    text: options.text,
  });
  return true;
}

export async function shareNativeText(options: {
  title: string;
  text: string;
  url: string;
}): Promise<boolean> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return false;
  await EqoverShare.shareText(options);
  return true;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('No fue posible preparar el archivo para compartir.'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const separator = result.indexOf(',');
      if (separator < 0) reject(new Error('No fue posible preparar el archivo para compartir.'));
      else resolve(result.slice(separator + 1));
    };
    reader.readAsDataURL(blob);
  });
}

export async function markNativeBundleReady(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (error) {
    console.error('No fue posible confirmar el bundle nativo.', error);
  }
}

export async function initializeNativeRuntime(router: Router): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  publishCachedCounts();
  await initializeOfflineReminders();
  if (offlineQueueCount.value > 0) void requestOfflineReminderPermission();
  const [{ identifier }, appInfo] = await Promise.all([Device.getId(), NativeApp.getInfo()]);
  nativeInstallationId = identifier;
  void refreshResponsibilityState();

  await NativeApp.addListener('appUrlOpen', ({ url }) => {
    void openDeepLink(router, url);
  });
  const launchUrl = await NativeApp.getLaunchUrl().catch(() => undefined);
  if (launchUrl?.url) void openDeepLink(router, launchUrl.url, true);

  if (Capacitor.getPlatform() === 'android') {
    await NativeApp.addListener('backButton', ({ canGoBack }) => {
      if (nativeOtaDeepLinkState.open) return;
      if (canGoBack) window.history.back();
      else void NativeApp.exitApp();
    });
  }

  await NativeApp.addListener('appStateChange', ({ isActive }) => {
    nativeAppActive.value = isActive;
    if (!isActive) {
      if (offlineQueueCount.value > 0) void scheduleOfflineReminders(offlineQueueCount.value);
      return;
    }
    void cancelOfflineReminders();
    refreshConnectivityAfterResume();
    void refreshResponsibilityState();
    if (lastPushToken) {
      void registerDevice({ value: lastPushToken }, identifier, appInfo.version, appInfo.build);
    }
  });
  const appState = await NativeApp.getState();
  nativeAppActive.value = appState.isActive;
  watchNativeHomeUpdate(router, nativeAppActive, nativeUpdateState, () => applyDownloadedNativeUpdate(true));

  window.addEventListener('eqover:api-token-changed', () => {
    if (!getApiToken()) {
      clearCachedPendingCount();
      clearCachedSurveyPendingCount();
    }
    void refreshResponsibilityState();
    if (lastPushToken) {
      void registerDevice({ value: lastPushToken }, identifier, appInfo.version, appInfo.build);
    }
  });
  window.addEventListener('eqover:responsibilities-changed', () => {
    void refreshResponsibilityState();
  });
  window.addEventListener('eqover:surveys-changed', () => {
    void refreshResponsibilityState();
  });
  window.addEventListener('eqover:widget-status-changed', () => {
    void syncResponsibilityBadge();
  });
  window.addEventListener('eqover:offline-queue-changed', (event) => {
    const pendingCount = Number((event as CustomEvent<{ pendingCount?: unknown }>).detail?.pendingCount ?? offlineQueueCount.value);
    if (pendingCount <= 0) {
      void cancelOfflineReminders();
      return;
    }
    if (nativeAppActive.value) void requestOfflineReminderPermission();
    else void scheduleOfflineReminders(pendingCount);
  });

  const pushNotificationsEnabled = Capacitor.getPlatform() === 'ios'
    ? import.meta.env.VITE_APNS_NOTIFICATIONS_ENABLED === 'true'
    : import.meta.env.VITE_PUSH_NOTIFICATIONS_ENABLED === 'true';
  if (!pushNotificationsEnabled) {
    console.warn('Push notifications are disabled because the native provider is not configured in this build.');
    return;
  }

  await PushNotifications.addListener('registration', (token) => {
    lastPushToken = token.value;
    void registerDevice(token, identifier, appInfo.version, appInfo.build);
  });
  await PushNotifications.addListener('registrationError', (error) => {
    console.error('No fue posible registrar notificaciones push.', error);
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    void openNotificationTarget(router, event);
  });
  await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    publishPendingCount(notification.data?.pendingCount);
  });

  const permission = await PushNotifications.checkPermissions();
  const receive = permission.receive === 'prompt'
    ? (await PushNotifications.requestPermissions()).receive
    : permission.receive;
  if (receive === 'granted') {
    await PushNotifications.register();
    await syncResponsibilityBadge();
  }
}

async function syncResponsibilityWidgetSession(): Promise<void> {
  if (!isResponsibilityWidgetNativeApp()) return;
  await EqoverWidget.configure({
    apiBaseUrl: apiBaseUrl() || 'https://mentor.report',
    accessToken: getApiToken() ?? '',
  }).catch((error) => console.error('No fue posible configurar el widget de pendientes.', error));
}

async function refreshResponsibilityState(): Promise<void> {
  await Promise.allSettled([
    syncResponsibilityWidgetSession(),
    syncResponsibilityBadge(),
  ]);
}

export function isAndroidNativeApp(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

export function isResponsibilityWidgetNativeApp(): boolean {
  return Capacitor.isNativePlatform() && ['android', 'ios'].includes(Capacitor.getPlatform());
}

export async function getLoadedAppVersion(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;

  const [currentBundle, appInfo] = await Promise.all([
    CapacitorUpdater.current().catch(() => undefined),
    NativeApp.getInfo().catch(() => undefined),
  ]);
  const otaVersion = currentBundle?.bundle.id !== 'builtin'
    ? currentBundle?.bundle.version.trim()
    : '';
  return otaVersion || currentBundle?.native.trim() || appInfo?.version.trim() || null;
}

export async function getResponsibilityWidgetStatus(): Promise<{
  installed: boolean;
  instances: number;
  pinSupported: boolean;
}> {
  if (!isResponsibilityWidgetNativeApp()) return { installed: true, instances: 0, pinSupported: false };
  return EqoverWidget.getStatus();
}

export async function requestResponsibilityWidgetPin(): Promise<{ supported: boolean; requested: boolean }> {
  if (!isResponsibilityWidgetNativeApp()) return { supported: false, requested: false };
  return EqoverWidget.requestPin();
}

async function registerDevice(token: Token, installationId: string, appVersion: string, appBuild: string): Promise<void> {
  const [deviceInfo, languageCode] = await Promise.all([
    Device.getInfo(),
    Device.getLanguageCode().then((value) => value.value).catch(() => undefined),
  ]);
  const accessToken = getApiToken();
  const response = await fetch(apiUrl('/api/mobile/devices'), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({
      installationId,
      token: token.value,
      provider: Capacitor.getPlatform() === 'ios' ? 'apns' : 'fcm',
      platform: Capacitor.getPlatform(),
      ...(Capacitor.getPlatform() === 'ios'
        ? { pushEnvironment: import.meta.env.VITE_APNS_ENVIRONMENT === 'sandbox' ? 'sandbox' : 'production' }
        : {}),
      model: deviceInfo.model,
      operatingSystem: deviceInfo.operatingSystem,
      osVersion: deviceInfo.osVersion,
      appVersion,
      appBuild,
      languageCode,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });
  if (!response.ok) console.error(`No fue posible registrar el dispositivo (HTTP ${response.status}).`);
  else await syncResponsibilityBadge();
}

async function openNotificationTarget(router: Router, event: ActionPerformed): Promise<void> {
  const target = String(event.notification.data?.path ?? '/webapp');
  if (!target.startsWith('/') || target.startsWith('//')) return;
  await router.push(target);
}

async function openDeepLink(router: Router, value: string, launch = false): Promise<void> {
  const otaVersion = nativeOtaDeepLinkVersion(value);
  if (otaVersion) {
    if (nativeUpdateState.phase === 'applying') return;
    nativeUpdates.clear();
    await otaDeepLinks.start(otaVersion, launch);
    return;
  }
  if (nativeOtaDeepLinkState.open) return;
  const path = nativeDeepLinkPath(value);
  if (path) await router.push(path);
}

async function syncResponsibilityBadge(): Promise<void> {
  if (responsibilityBadgeSync) return responsibilityBadgeSync;
  responsibilityBadgeSync = performResponsibilityBadgeSync().finally(() => {
    responsibilityBadgeSync = null;
  });
  return responsibilityBadgeSync;
}

async function performResponsibilityBadgeSync(): Promise<void> {
  const accessToken = getApiToken();
  if (!accessToken) return;
  const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
  let widgetStatusToConfirm: boolean | undefined;
  let widgetStatusCacheKey = '';
  if (nativeInstallationId) {
    headers['X-Eqover-Mobile-Installation'] = nativeInstallationId;
    headers['X-Eqover-Mobile-Platform'] = Capacitor.getPlatform();
  }
  if (isResponsibilityWidgetNativeApp() && nativeInstallationId) {
    const status = await EqoverWidget.getStatus().catch(() => null);
    if (status) {
      const activeUserId = localStorage.getItem('eqover-dashboard-user') ?? 'session';
      widgetStatusCacheKey = `${reportedWidgetStatusPrefix}${activeUserId}:${nativeInstallationId}`;
      if (localStorage.getItem(widgetStatusCacheKey) !== String(status.installed)) {
        widgetStatusToConfirm = status.installed;
        headers['X-Eqover-Widget-Installed'] = String(status.installed);
      }
    }
  }
  const response = await fetch(apiUrl('/api/mobile/responsibilities/badge'), {
    cache: 'no-store',
    headers,
  }).catch(() => null);
  if (!response?.ok) return;
  if (widgetStatusToConfirm !== undefined && widgetStatusCacheKey) {
    localStorage.setItem(widgetStatusCacheKey, String(widgetStatusToConfirm));
  }
  const payload = await response.json() as {
    data?: { pendingCount?: number; surveyPendingCount?: number; responsibilities?: ResponsibilityWidgetItem[] };
  };
  const pendingCount = Math.max(0, Number(payload.data?.pendingCount ?? 0));
  const surveyPendingCount = Math.max(0, Number(payload.data?.surveyPendingCount ?? 0));
  publishPendingCount(pendingCount);
  publishSurveyPendingCount(surveyPendingCount);
  if (isResponsibilityWidgetNativeApp()) {
    const responsibilities = Array.isArray(payload.data?.responsibilities)
      ? payload.data.responsibilities.slice(0, 3)
      : [];
    await EqoverWidget.updateSnapshot({ pendingCount, responsibilities }).catch((error) => {
      console.error('No fue posible compartir los pendientes con el widget.', error);
    });
  }
  if (pendingCount === 0) await PushNotifications.removeAllDeliveredNotifications().catch(() => undefined);
}

function publishPendingCount(value: unknown): void {
  const pendingCount = Number(value);
  if (!Number.isFinite(pendingCount) || pendingCount < 0) return;
  cachePendingCount(pendingCount);
  window.dispatchEvent(new CustomEvent('eqover:responsibility-count', { detail: { pendingCount } }));
}

function publishSurveyPendingCount(value: unknown): void {
  const surveyPendingCount = Number(value);
  if (!Number.isFinite(surveyPendingCount) || surveyPendingCount < 0) return;
  cacheSurveyPendingCount(surveyPendingCount);
  window.dispatchEvent(new CustomEvent('eqover:survey-count', { detail: { surveyPendingCount } }));
}

export function readCachedPendingCount(): number {
  const activeUserId = localStorage.getItem('eqover-dashboard-user') ?? '';
  const keys = activeUserId
    ? [`${pendingCountCachePrefix}${activeUserId}`]
    : [`${pendingCountCachePrefix}session`];
  for (const key of keys) {
    try {
      const value = JSON.parse(localStorage.getItem(key) ?? 'null') as { pendingCount?: unknown } | null;
      const pendingCount = Number(value?.pendingCount);
      if (Number.isFinite(pendingCount) && pendingCount >= 0) return pendingCount;
    } catch { /* Ignora una copia dañada. */ }
  }
  return 0;
}

export function cachePendingCount(value: number): void {
  const pendingCount = Math.max(0, Math.floor(value));
  const snapshot = JSON.stringify({ pendingCount, updatedAt: new Date().toISOString() });
  const activeUserId = localStorage.getItem('eqover-dashboard-user') ?? '';
  localStorage.setItem(`${pendingCountCachePrefix}session`, snapshot);
  if (activeUserId) localStorage.setItem(`${pendingCountCachePrefix}${activeUserId}`, snapshot);
}

export function clearCachedPendingCount(userId = localStorage.getItem('eqover-dashboard-user') ?? ''): void {
  localStorage.removeItem(`${pendingCountCachePrefix}session`);
  if (userId) localStorage.removeItem(`${pendingCountCachePrefix}${userId}`);
}

export function readCachedSurveyPendingCount(): number {
  const activeUserId = localStorage.getItem('eqover-dashboard-user') ?? '';
  const keys = activeUserId
    ? [`${surveyPendingCountCachePrefix}${activeUserId}`]
    : [`${surveyPendingCountCachePrefix}session`];
  for (const key of keys) {
    try {
      const value = JSON.parse(localStorage.getItem(key) ?? 'null') as { surveyPendingCount?: unknown } | null;
      const surveyPendingCount = Number(value?.surveyPendingCount);
      if (Number.isFinite(surveyPendingCount) && surveyPendingCount >= 0) return surveyPendingCount;
    } catch { /* Ignora una copia dañada. */ }
  }
  return 0;
}

export function cacheSurveyPendingCount(value: number): void {
  const surveyPendingCount = Math.max(0, Math.floor(value));
  const snapshot = JSON.stringify({ surveyPendingCount, updatedAt: new Date().toISOString() });
  const activeUserId = localStorage.getItem('eqover-dashboard-user') ?? '';
  localStorage.setItem(`${surveyPendingCountCachePrefix}session`, snapshot);
  if (activeUserId) localStorage.setItem(`${surveyPendingCountCachePrefix}${activeUserId}`, snapshot);
}

export function clearCachedSurveyPendingCount(userId = localStorage.getItem('eqover-dashboard-user') ?? ''): void {
  localStorage.removeItem(`${surveyPendingCountCachePrefix}session`);
  if (userId) localStorage.removeItem(`${surveyPendingCountCachePrefix}${userId}`);
}

function publishCachedCounts(): void {
  const pendingCount = readCachedPendingCount();
  const surveyPendingCount = readCachedSurveyPendingCount();
  window.dispatchEvent(new CustomEvent('eqover:responsibility-count', { detail: { pendingCount } }));
  window.dispatchEvent(new CustomEvent('eqover:survey-count', { detail: { surveyPendingCount } }));
}
